import type { CreditLotEntry } from '@nadoutong/contracts';
import { useCredits } from '../api/useBilling.ts';
import { usePromoterBinding } from '../api/usePromoter.ts';
import { useT } from '../i18n/index.ts';
import { daysUntil, formatTimestamp, formatUsdMicrosAsCnyDisplay } from './formatters.ts';

/**
 * "Includes ¥X referral credit · expires {date} · expiring soon" — the
 * billing-card sibling of `GiftBalanceLine` (tenant #192).
 *
 * Shown only when the account is bound and `grantStatus` is `issued`.
 * Amount and expiry prefer a `promoter_grant` lot when the credits
 * payload carries one (optional `sourceType`, not yet on the public
 * DTO); otherwise they come from the binding view. If neither has an
 * amount, the line is status-only.
 *
 * A grant inside 3 days of expiry gets the warn marker the backend
 * reminder uses. Upstream identity never appears.
 */

const EXPIRING_SOON_DAYS = 3;

interface CreditLotWithSource extends CreditLotEntry {
  readonly sourceType?: string;
}

export function PromoterGrantLine({
  cnyPerUsd,
}: {
  readonly cnyPerUsd: number | null;
}): JSX.Element | null {
  const t = useT();
  const bindingQ = usePromoterBinding();
  const creditsQ = useCredits();

  if (bindingQ.data === undefined) {
    return null;
  }
  const { binding } = bindingQ.data;
  if (!binding.bound || binding.grantStatus !== 'issued') {
    return null;
  }

  const fromLot = promoterGrantFromLots(creditsQ.data?.lots ?? []);
  const remainingUsdMicros = fromLot?.remainingUsdMicros ?? binding.grantRemainingUsdMicros;
  const expiresAt = fromLot?.expiresAt ?? binding.grantExpiresAt;
  const days = daysUntil(expiresAt);
  const expiringSoon = days !== null && days >= 0 && days <= EXPIRING_SOON_DAYS;

  const amountLabel =
    remainingUsdMicros !== null && remainingUsdMicros > 0
      ? formatUsdMicrosAsCnyDisplay(remainingUsdMicros, cnyPerUsd)
      : null;
  const included =
    amountLabel === null
      ? t('promoter.grant.status_issued')
      : t('promoter.grant.included', { amount: amountLabel });
  const expiry =
    expiresAt !== null && expiresAt.length > 0
      ? t('promoter.grant.expires_on', { date: formatTimestamp(expiresAt) })
      : null;

  const parts = [included];
  if (expiry !== null) {
    parts.push(expiry);
  }

  return (
    <span
      className={`block font-mono text-mono-label ${expiringSoon ? 'text-warn' : 'text-ink-mid'}`}
      data-testid="billing-balance-promoter-grant"
    >
      {parts.join(' · ')}
      {expiringSoon ? (
        <span data-testid="billing-promoter-grant-expiring">
          {` · ${t('promoter.grant.expiring_soon')}`}
        </span>
      ) : null}
    </span>
  );
}

function promoterGrantFromLots(lots: readonly CreditLotEntry[]): CreditLotEntry | null {
  for (const lot of lots) {
    const widened: CreditLotWithSource = lot;
    if (widened.sourceType === 'promoter_grant') {
      return lot;
    }
  }
  return null;
}
